import { Component, Input, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  AlertController,
  Events,
  LoadingController,
  NavController,
  Platform,
  PopoverController,
} from "ionic-angular";
import { ConfigurationService } from "providers/configuration/configuration.service";
import { Observable } from "rxjs";
import "rxjs/add/observable/from";
import "rxjs/add/operator/mergeMap";
import "rxjs/add/operator/toArray";
import { AssessmentDataService } from "../../../../providers/asmt-data-service/asmt-data-service";
import { StateManagementService } from "../../../../providers/state-management/state-management.service";
import { WoundHistoryPopoverComponent } from "../../../wound-history-popover/wound-history-popover";

export const NEW_WOUND_SITE_CARD_ID = "CL010.284";
@Component({
  selector: "wound-history-card",
  templateUrl: "wound-history-card.html",
})
export class WoundHistoryCardComponent implements OnInit {
  protected spinnerCounter: number = 0;
  protected spinner: any = null;
  historyData = [];

  @Input() assessmentId: string;
  @Input() sectionId: string;
  @Input() config;
  @Input() disabled: boolean = false;
  @Input() newWoundSiteCard: any;

  historyApiData: any[] = [];
  id: string;
  label: string;
  access: string;
  declinable: boolean;
  locationText: string;
  status: string;
  keys: string[];

  constructor(
    public navCtrl: NavController,
    private loadingCtrl: LoadingController,
    public translate: TranslateService,
    public asmtDataService: AssessmentDataService,
    public events: Events,
    public popover: PopoverController,
    public state: StateManagementService,
    public alertCtrl: AlertController,
    public translatePipe: TranslateService,
    private platform: Platform,
    public configService: ConfigurationService
  ) {}

  ngOnInit(): void {
    this.id = this.config.id;
  }

  expandData() {
    if (this.historyData) {
      for (let i = 0; i < this.historyData.length; i++) {
        if (this.historyData[i].expanded !== true) {
          this.historyData[i].expanded = false;
        }
      }
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  viewMore(row) {
    row.expanded = !row.expanded;
  }

  @Input() set data(incomingData: any) {
    if (incomingData && incomingData.data[0].keys.length > 0) {
      this.historyData = [];
      this.historyApiData = incomingData.data;
      this.keys = this.historyApiData[0].keys;
      var latestKey = this.keys[0];

      let historyData = this.historyApiData[0].history[latestKey];
      this.locationText =
        historyData[0].locationIndex +
        1 +
        ". " +
        historyData[0].stepOneSidebarData.locationTitle +
        " (" +
        historyData[0].stepOneSidebarData.locationType.charAt(0).toUpperCase() +
        historyData[0].stepOneSidebarData.locationType.slice(1) +
        ")";
      for (var i = 0; i < historyData.length; i++) {
        historyData[i].measurement = this.getMeasurement(historyData[i]);
        this.historyData.push(historyData[i]);
      }
      this.historyData.sort(this.compare);
      this.status = this.historyData[0].woundStatus;
    }
  }

  presentMoreOptionsOnWoundHistory($event) {
    let popover = this.popover.create(
      WoundHistoryPopoverComponent,
      {
        keys: this.keys,
      },
      { cssClass: "wound-history-popover.scss" }
    );
    popover.present({
      ev: $event,
    });
    popover.onDidDismiss((data) => {
      if (data) {
        this.filterCardDataBasedOnKey(data);
      }
    });
  }

  filterCardDataBasedOnKey(key: string) {
    this.historyData = [];
    this.locationText = "";
    this.status = "";

    if (key === null || key === undefined) {
      this.locationText = "";
    } else {
      let historyData = this.historyApiData[0].history[key];
      this.keys = this.historyApiData[0].keys;
      for (var i = 0; i < historyData.length; i++) {
        historyData[i].measurement = this.getMeasurement(historyData[i]);
        this.historyData.push(historyData[i]);
      }
      this.historyData.sort(this.compare);
      this.status = this.historyData[0].woundStatus;
      this.locationText =
        historyData[0].locationIndex +
        1 +
        ". " +
        historyData[0].stepOneSidebarData.locationTitle +
        " (" +
        historyData[0].stepOneSidebarData.locationType.charAt(0).toUpperCase() +
        historyData[0].stepOneSidebarData.locationType.slice(1) +
        ")";
    }
  }

  getMeasurement(historyData: any) {
    let measurement = "Unable to Measure";
    if (!historyData.stepOneSidebarData.unableToMeasure) {
      measurement = "";
      let width = historyData.stepOneSidebarData.width;
      let length = historyData.stepOneSidebarData.length;
      let depth = historyData.stepOneSidebarData.depth;
      measurement = measurement.concat(
        "W-".concat(!!width ? width + ", " : "x, ")
      );
      measurement = measurement.concat(
        "L-".concat(!!length ? length + ", " : "x, ")
      );
      measurement = measurement.concat("D-".concat(!!depth ? depth + "" : "x"));
    }
    return measurement;
  }

  // SUP-19942: every row must show the clinician for THAT entry (product
  // decision, Rob Jackson). The SUP-19891 fix resolved both columns from the
  // wound's ORIGIN row, so all of a wound's entries collapsed onto one date and
  // one name — which is what the agencies reported as "measurements not sticking".
  //
  // Date is bound directly to the row's own createdDte in the template; only the
  // clinician needs a lookup:
  //   - `documentedBy` is stamped per save by new-wound-site-tabs (SUP-19942).
  //   - Entries documented before that shipped have none. They fall back to
  //     `createdBy` — the clinician who owned the visit the entry came from
  //     (SUP-19942 comment 309982, option 1). Right in the large majority of
  //     cases; measured ~1% of entries where the visit owner was not the
  //     documenter.
  getEnteredBy(history: any): string {
    return history.documentedBy || history.createdBy || "";
  }

  // SUP-19942: `createdDte` arrives as Java `Date.toString()`
  // ("Wed Aug 05 16:26:37 UTC 2026") because mapAssessmentWithVisits puts a
  // java.util.Date into the JSON and org.json falls back to toString(). Comparing
  // that as a STRING orders by weekday name, then month name, and never reaches the
  // year — chart 92011 rendered 05/06, 05/26, 06/23, 07/06, 05/15 with the newest
  // entry fourth. Parse to a timestamp instead.
  //
  // `new Date()` is deliberately the same parse Angular's `date` pipe applies to
  // render this column, so the sort key and the displayed value cannot drift apart.
  compare(a, b) {
    const timeA = new Date(a.createdDte).getTime();
    const timeB = new Date(b.createdDte).getTime();

    // An unparseable date sorts last rather than scrambling the whole list.
    if (isNaN(timeA) && isNaN(timeB)) {
      return 0;
    }
    if (isNaN(timeA)) {
      return 1;
    }
    if (isNaN(timeB)) {
      return -1;
    }
    return timeB - timeA;
  }
  discardWoundChangesPopup() {
    let alert = this.alertCtrl.create({
      title: this.translatePipe.instant("discardWoundChanges"),
      buttons: [
        {
          text: this.translatePipe.instant("cancel"),
          cssClass: this.platform.is("ios")
            ? "alert-greenbtn"
            : "alert-paddingright100 alert-greenbtn text-captilize",
          handler: () => {
            this.configService.rum.addAction(
              "Wound History Card / Discard Cancel"
            );
          },
        },
        {
          text: this.translatePipe.instant("proceedButton"),
          cssClass: "text-captilize",
          handler: () => {
            // Track proceed and cancel actions via Datadog RUM
            this.configService.rum.addAction(
              "Wound History Card / Discard Proceed"
            );
            const requests$: Observable<any>[] = [];

            // This handler collects requests that will be made in order to discard the wouund and wound history cards.
            // It publishes these requests (as observables) where they will ultimately be handled by `section.ts`.
            // The reason for this is that `section.ts` will need to save the section and then reload the data.
            // `section.ts` will need to do this to preserve order of operations.

            // Delete request: new wound site cards
            if (this.newWoundSiteCard?.data) {
              for (const newWoundSiteCard of this.newWoundSiteCard?.data) {
                requests$.push(
                  this.asmtDataService.deleteRecord(
                    this.assessmentId,
                    this.sectionId,
                    NEW_WOUND_SITE_CARD_ID,
                    newWoundSiteCard.id
                  )
                );
              }
            }

            // Delete requests: wound history (should be one)
            if (this.historyApiData) {
              for (const historyCard of this.historyApiData) {
                if (historyCard.id) {
                  requests$.push(
                    this.asmtDataService.deleteRecord(
                      this.assessmentId,
                      this.sectionId,
                      this.id,
                      historyCard.id
                    )
                  );
                }
              }
            }

            // Publish event with observable that will make requests
            if (requests$.length > 0) {
              const discardWoundHistory$ = Observable.from(requests$)
                .mergeMap((request) => request, 3)
                .toArray();
              this.events.publish("discardWoundHistory", discardWoundHistory$);

              // Clear local data immediately to prevent duplicate delete requests
              this.historyData = [];
              this.historyApiData = [];
              this.newWoundSiteCard = null;
            }
          },
        },
      ],
    });
    alert.present();
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/cards/wound-history-card/wound-history-card.ts